import { SupabaseClient } from "@supabase/supabase-js";
import { logException } from "@punchup/sentry";
import { sanitizeIlikeSearch } from "@punchup/utils/sanitizeIlikeSearch";
import { Database } from "../../supabase.types";
import {
  ComedianManagerEntry,
  ComedianManagerExportRow,
  ComedianManagersResult,
  ComedianSearchResult,
} from "../../models/comedian-managers";

// Supabase caps a single query at 1000 rows, so bulk reads page through.
const PAGE_SIZE = 1000;
// `.in(...)` filters are batched so the IN list never grows unbounded.
const ID_BATCH_SIZE = 100;
// Cap on search results returned to the lookup box.
const SEARCH_LIMIT = 25;

type Supabase = SupabaseClient<Database>;

/**
 * Resolves a set of admin user ids to their contact info from `users_extra`,
 * batched to keep each IN list bounded. Users without a `users_extra` row are
 * simply absent from the returned map.
 */
async function fetchManagerContacts(
  supabase: Supabase,
  adminIds: string[]
): Promise<
  Map<
    string,
    { email: string; phone: string | null; lastActiveAt: string | null }
  >
> {
  const contacts = new Map<
    string,
    { email: string; phone: string | null; lastActiveAt: string | null }
  >();

  for (let i = 0; i < adminIds.length; i += ID_BATCH_SIZE) {
    const chunk = adminIds.slice(i, i + ID_BATCH_SIZE);
    const { data, error } = await supabase
      .from("users_extra")
      .select("id, email, phone, last_active_at")
      .in("id", chunk);

    if (error) {
      logException(error, {
        data: "[comedian-managers] Failed to resolve manager contacts",
      });
      throw error;
    }

    for (const row of data ?? []) {
      contacts.set(row.id, {
        email: row.email ?? "",
        phone: row.phone ?? null,
        lastActiveAt: row.last_active_at ?? null,
      });
    }
  }

  return contacts;
}

/**
 * Searches comedian pages by display name for the internal lookup box.
 * Case-insensitive substring match; capped at {@link SEARCH_LIMIT} results.
 */
export async function searchComediansByName(
  supabase: Supabase,
  query: string,
  limit: number = SEARCH_LIMIT
): Promise<ComedianSearchResult[]> {
  const safe = sanitizeIlikeSearch(query);
  if (safe.length === 0) return [];

  const { data, error } = await supabase
    .from("comedians")
    .select("id, display_name, slug, is_punchup_verified, published_at")
    .ilike("display_name", `%${safe}%`)
    .order("display_name", { ascending: true })
    .limit(limit);

  if (error) {
    logException(error, {
      data: "[comedian-managers] Failed to search comedians",
    });
    throw error;
  }

  return (data ?? []).map((c) => ({
    comedianId: c.id,
    displayName: c.display_name,
    slug: c.slug,
    isPunchupVerified: c.is_punchup_verified,
    publishedAt: c.published_at,
  }));
}

/**
 * Returns one comedian page with the full list of its managers/admins
 * (resolved to email/phone/last-active), sorted by email. Returns null if no
 * comedian exists for the given id.
 */
export async function getComedianManagers(
  supabase: Supabase,
  comedianId: string
): Promise<ComedianManagersResult | null> {
  const { data: comedian, error: comedianError } = await supabase
    .from("comedians")
    .select("id, display_name, slug")
    .eq("id", comedianId)
    .maybeSingle();

  if (comedianError) {
    logException(comedianError, {
      data: "[comedian-managers] Failed to load comedian",
    });
    throw comedianError;
  }
  if (!comedian) return null;

  // comedian_dashboard_admins is a small per-comedian set, but page anyway.
  const adminIds: string[] = [];
  let offset = 0;
  while (true) {
    const { data, error } = await supabase
      .from("comedian_dashboard_admins")
      .select("admin_id")
      .eq("comedian_id", comedianId)
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      logException(error, {
        data: "[comedian-managers] Failed to load comedian_dashboard_admins",
      });
      throw error;
    }
    if (!data || data.length === 0) break;
    for (const row of data) adminIds.push(row.admin_id);
    offset += data.length;
    if (data.length < PAGE_SIZE) break;
  }

  const contacts = await fetchManagerContacts(supabase, adminIds);

  const managers: ComedianManagerEntry[] = adminIds.map((userId) => {
    const contact = contacts.get(userId);
    return {
      userId,
      email: contact?.email ?? "",
      phone: contact?.phone ?? null,
      lastActiveAt: contact?.lastActiveAt ?? null,
    };
  });
  managers.sort((a, b) => a.email.localeCompare(b.email));

  return {
    comedianId: comedian.id,
    comedianName: comedian.display_name,
    comedianSlug: comedian.slug,
    managers,
  };
}

/**
 * Builds the full export of every comedian↔manager pairing across all comedian
 * pages, as flat rows for CSV/clipboard. Pages through the join table once,
 * then batch-resolves comedian names and manager contacts. Comedians with no
 * managers contribute no rows.
 */
export async function getAllComedianManagers(
  supabase: Supabase
): Promise<ComedianManagerExportRow[]> {
  // 1. Page the join table → comedianId -> set of admin ids.
  const comedianToAdmins = new Map<string, Set<string>>();
  let offset = 0;
  while (true) {
    const { data, error } = await supabase
      .from("comedian_dashboard_admins")
      .select("comedian_id, admin_id")
      .range(offset, offset + PAGE_SIZE - 1);

    if (error) {
      logException(error, {
        data: "[comedian-managers] Failed to page comedian_dashboard_admins",
      });
      throw error;
    }
    if (!data || data.length === 0) break;
    for (const row of data) {
      let set = comedianToAdmins.get(row.comedian_id);
      if (!set) {
        set = new Set<string>();
        comedianToAdmins.set(row.comedian_id, set);
      }
      set.add(row.admin_id);
    }
    offset += data.length;
    if (data.length < PAGE_SIZE) break;
  }

  if (comedianToAdmins.size === 0) return [];

  const comedianIds = [...comedianToAdmins.keys()];
  const adminIds = [
    ...new Set([...comedianToAdmins.values()].flatMap((s) => [...s])),
  ];

  // 2. Batch-resolve comedian names/slugs for only the comedians that appear.
  const comedianInfo = new Map<string, { name: string; slug: string }>();
  for (let i = 0; i < comedianIds.length; i += ID_BATCH_SIZE) {
    const chunk = comedianIds.slice(i, i + ID_BATCH_SIZE);
    const { data, error } = await supabase
      .from("comedians")
      .select("id, display_name, slug")
      .in("id", chunk);

    if (error) {
      logException(error, {
        data: "[comedian-managers] Failed to resolve comedian names",
      });
      throw error;
    }
    for (const row of data ?? []) {
      comedianInfo.set(row.id, { name: row.display_name, slug: row.slug });
    }
  }

  // 3. Batch-resolve manager contacts.
  const contacts = await fetchManagerContacts(supabase, adminIds);

  // 4. Flatten to one row per (comedian, manager).
  const rows: ComedianManagerExportRow[] = [];
  for (const [comedianId, admins] of comedianToAdmins) {
    const comedian = comedianInfo.get(comedianId);
    if (!comedian) continue;
    for (const userId of admins) {
      const contact = contacts.get(userId);
      rows.push({
        comedianName: comedian.name,
        comedianSlug: comedian.slug,
        comedianId,
        managerEmail: contact?.email ?? "",
        managerUserId: userId,
        managerPhone: contact?.phone ?? null,
        managerLastActiveAt: contact?.lastActiveAt ?? null,
      });
    }
  }

  rows.sort(
    (a, b) =>
      a.comedianName.localeCompare(b.comedianName) ||
      a.managerEmail.localeCompare(b.managerEmail)
  );

  return rows;
}
